import React from 'react';
import { 
  course_navigation_back,
  course_previousLecture,
  course_completeCourse,
  course_nextLecture,
  course_toggleToc
} from '@paraglide/messages';
import { useTheme } from '@emotion/react';

// Import icons from lucide-react
import ArrowLeft from 'lucide-react/dist/esm/icons/arrow-left';
import Menu from 'lucide-react/dist/esm/icons/menu';
import ChevronLeft from 'lucide-react/dist/esm/icons/chevron-left';
import ChevronRight from 'lucide-react/dist/esm/icons/chevron-right';
import CheckSquare from 'lucide-react/dist/esm/icons/check-square';

// Import types
import { SectionDetail, LectureDetailResponseContentTypeEnum } from '@edusense/course-client';

// Import styled components
import {
  RootContainer,
  Header,
  HeaderContentWrapper,
  HeaderLeft,
  HeaderRight,
  StyledButton,
  CompleteButton,
  StyledIconButton,
  MainContentArea,
  MaxWidthContentWrapper,
  MainViewerContainer,
  SidebarContainer,
  LectureTitle
} from './LectureTemplate.styles';

// Import actual components from course features package
import { LectureViewer } from '@edusense/features-course';
import { LectureSidebar } from '@edusense/features-course';

// Type definitions
interface LectureDisplayData {
  id: string;
  title: string;
  videoUrl?: string;
  description?: string;
  markdownContent?: string;
  contentType?: LectureDetailResponseContentTypeEnum;
}

interface LectureTemplateProps {
  courseId: string;
  currentLectureId: string;
  lecture: LectureDisplayData;
  curriculum: SectionDetail[];
  isTocVisible: boolean;
  onToggleToc: () => void;
  onGoBack: () => void;
  onNavigatePrevious?: () => void;
  onNavigateNext?: () => void;
  isPreviousEnabled: boolean;
  isNextEnabled: boolean;
  isMarkdownLoading?: boolean;
  markdownError?: string;
  onQuizSubmit: (
    answers: Record<string, any>,
    results: Record<string, 'correct' | 'incorrect' | 'pending'>
  ) => void;
  onCompleteCourse?: () => void;
  isLastLecture?: boolean;
}

const LectureTemplate: React.FC<LectureTemplateProps> = ({
  courseId,
  currentLectureId,
  lecture,
  curriculum,
  isTocVisible,
  onToggleToc,
  onGoBack,
  onNavigatePrevious,
  onNavigateNext,
  isPreviousEnabled,
  isNextEnabled,
  isMarkdownLoading,
  markdownError,
  onQuizSubmit,
  onCompleteCourse,
  isLastLecture,
}) => {
  const theme = useTheme();

  // Effect to set header height for sidebar positioning
  React.useEffect(() => {
    const headerElement = document.querySelector('header');
    if (headerElement) {
      const height = headerElement.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);

  return (
    <RootContainer>
      <Header>
        <HeaderContentWrapper>
          <HeaderLeft>
            <StyledIconButton onClick={onGoBack} aria-label={course_navigation_back()}>
              <ArrowLeft size={20} />
            </StyledIconButton>
            <LectureTitle title={lecture.title}>{lecture.title}</LectureTitle>
          </HeaderLeft>
          <HeaderRight>
            <StyledButton
              onClick={onNavigatePrevious}
              disabled={!isPreviousEnabled}
            >
              <ChevronLeft size={18} />
              <span>{course_previousLecture()}</span>
            </StyledButton>
            {isLastLecture ? (
              <CompleteButton
                onClick={onCompleteCourse}
                disabled={!onCompleteCourse}
              >
                <span>{course_completeCourse()}</span>
                <CheckSquare size={18} />
              </CompleteButton>
            ) : (
              <StyledButton
                onClick={onNavigateNext}
                disabled={!isNextEnabled}
              >
                <span>{course_nextLecture()}</span>
                <ChevronRight size={18} />
              </StyledButton>
            )}
            <StyledIconButton onClick={onToggleToc} aria-label={course_toggleToc()}>
              <Menu size={20} />
            </StyledIconButton>
          </HeaderRight>
        </HeaderContentWrapper>
      </Header>
      <MainContentArea>
        <MaxWidthContentWrapper>
          <MainViewerContainer>
            <LectureViewer
              lecture={lecture as any}
              isLoading={isMarkdownLoading ?? false}
              error={markdownError ?? null}
              onQuizSubmit={onQuizSubmit}
            />
          </MainViewerContainer>
          <SidebarContainer isVisible={isTocVisible}>
            <LectureSidebar
              courseId={courseId}
              curriculum={curriculum}
              currentLectureId={currentLectureId}
            />
          </SidebarContainer>
        </MaxWidthContentWrapper>
      </MainContentArea>
    </RootContainer>
  );
};

export default LectureTemplate;